import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

import WidgetShell from '../WidgetShell';
import Spinner from '../../Spinner';
import SeloApoiadorInline from '../../SeloApoiadorInline';
import axios from '../../../utils/axios';

const SuggestionsWidget = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data, isLoading, isError, refetch } = useQuery(
    ['suggestions', 'users'],
    async () => {
      const response = await axios.get('/api/users/suggestions', {
        params: { limit: 5 },
      });
      return response.data;
    },
    {
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );

  const followMutation = useMutation(
    ({ followeeId }) =>
      axios.patch('/api/users/follow', {
        followeeId,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['suggestions', 'users']);
        queryClient.invalidateQueries(['posts', 'feed']);
        toast.success(t('rightSidebar.widgets.suggestions.followToast'));
      },
      onError: () => {
        toast.error(t('rightSidebar.widgets.suggestions.followError'));
      },
    }
  );

  const users = useMemo(() => data?.users || [], [data]);

  return (
    <WidgetShell title={t('rightSidebar.widgets.suggestions.title')}>
      {isLoading && (
        <div className="flex items-center justify-center py-6">
          <Spinner />
        </div>
      )}
      {!isLoading && isError && (
        <div className="px-4 py-4 text-sm text-on-surface/70">
          {t('rightSidebar.widgets.suggestions.error')}{' '}
          <button
            type="button"
            onClick={() => refetch()}
            className="text-primary hover:underline"
          >
            {t('rightSidebar.widgets.suggestions.retry')}
          </button>
        </div>
      )}
      {!isLoading && !isError && users.length === 0 && (
        <p className="px-4 py-4 text-sm text-on-surface/70">
          {t('rightSidebar.widgets.suggestions.empty')}
        </p>
      )}
      {!isLoading && !isError && users.length > 0 && (
        <ul className="flex flex-col">
          {users.map((user) => (
            <li
              key={user.id}
              className="flex items-center gap-3 px-3 py-2 hover:bg-on-surface/5"
            >
              <Link
                to={`/${user.username}`}
                className="flex items-center gap-3 flex-1 min-w-0"
              >
                <img
                  src={user.profile?.img}
                  alt={user.profile?.name || user.username}
                  className="h-10 w-10 rounded-full object-cover shrink-0"
                />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-on-surface truncate inline-flex items-center gap-1 max-w-full">
                    <span className="truncate">
                      {user.profile?.name || user.username}
                    </span>
                    <SeloApoiadorInline selo={user.apoiadorSelo} size="xs" />
                  </p>
                  <p className="text-xs text-on-surface/60 truncate">
                    @{user.username}
                  </p>
                </div>
              </Link>
              <button
                type="button"
                onClick={() => followMutation.mutate({ followeeId: user.id })}
                disabled={followMutation.isLoading}
                className="shrink-0 px-3 py-1 text-xs font-semibold rounded-full bg-on-surface text-surface hover:bg-on-surface/80 disabled:opacity-50"
              >
                {t('rightSidebar.widgets.suggestions.follow')}
              </button>
            </li>
          ))}
        </ul>
      )}
    </WidgetShell>
  );
};

export default SuggestionsWidget;
